/**
 * Updated for new UI design (v2)
 * Changes made:
 * 1. Rendering `MedicalCodesManager` for diagnosis codes for shared invoices.
 * 2. Rendering a separate `MedicalCodesManager` for each code type (procedure code,
 *    modifier codes, and diagnosis codes)for other invoice typess *
 */

import React from 'react';
import { commonEnums } from '@codametrix/ui-common';
import classnames from 'classnames';
import { CMXIcon } from '@codametrix/ui-components';
import { MedicalCodesManager } from '../../transacton-code/medicalcodes-manager';
import { UnitInput } from '@codametrix/ui-components';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuButton,
  Box
} from '@chakra-ui/react';
import { Conditional } from '@codametrix/ui-components';
import { getLedgerChangedCodes, getLedgerTransaction, getLedgerTransactionsFromInvoice } from '../../../utils/utils';
import './styles/index.scss';
const { ChargeSessionCodeType, CodeSearchType } = commonEnums;
const { EditorIssueLevel } = commonEnums;

/**
 * React component that displays a Header
 * @param medicalCode
 * @param errors
 * @param handleCodeDelete
 * @param handleEnableTechPro
 * @param handleDisableTechPro
 * @param handleTransactionReorder
 * @returns
 */
export const TransactionHeader = (
  procedureCode: CMxCommonApp.ChargeSessionCode,
  techTransaction: CMxCommonApp.TechTransaction,
  errors: AmplifyAPI.AmplifyError[],
  invoice: CMxCommonApp.CodableInvoice,
  codeEdits: CMxCommonApp.CodeEditInputs,
  handleUnitsChange: (units: number, unitElement: HTMLElement) => void,
  handleTransactionReorder: (
    transactionCurrentIndex: number,
    transactionDestinationIndex: number
  ) => void,
  handleCodeDelete: (ev: any) => void,
  handleCodeChecked: (ev: any) => void,
  handleEnableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void,
  handleDisableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void,
  codeDropHandler: (codes: CMxCommonApp.ChargeSessionCode[]) => void,
  isTimeBasedCode: () => boolean,
  timeBasedCodePopup: () => React.ReactElement,
  onSearch: (codeQuery: CMxCommonApp.CodeQuery) => void,
  onSearchSelection: (codeQuery: Accelerate.CodeActionPayload) => void,
  onAddAllCodes: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void,
  onRemoveAllCodes: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void,
  searchResults: Accelerate.CodeSearchResult,
  codableTransaction: CMxCommonApp.CodableTransaction,
  transaction: CMxCommonApp.SimplifiedTransaction,
  editable: boolean,
  suggestedCodes: CMxCommonApp.SuggestedCodes,
  autoFocus: boolean,
  transactionIndex: number,
  codeSourcesLedgerEnabled: boolean
) => {
  const isFirstTnx = transactionIndex === 0;
  const isLastTnx = transactionIndex === invoice?.transactions.length - 1;

  let dianosisCodeList = [];
  let modifierCodeList = [];
  let fieldDefinitions = [];
  const unitsRef: React.RefObject<HTMLDivElement> = React.createRef();

  const source = codableTransaction || transaction;

  if (source) {
    dianosisCodeList = source.codes.filter(
      (item: CMxCommonApp.ChargeSessionCode) =>
        item.codeType === ChargeSessionCodeType.DIAGNOSIS
    );

    modifierCodeList = source.codes.filter(
      (item: CMxCommonApp.ChargeSessionCode) =>
        item.codeType === ChargeSessionCodeType.MODIFIER
    );

    if (codableTransaction) {
      fieldDefinitions = codableTransaction.fieldDefinitions;
    }
  }
  // for shared-invoice display only diagnosis medical codes manager
  if (invoice?.type == commonEnums.InvoiceTypes.SHARED_CODES) {
    return (
      <MedicalCodesManager
        codeType={ChargeSessionCodeType.DIAGNOSIS}
        codeList={dianosisCodeList}
        searchResults={searchResults?.[CodeSearchType.ICD]}
        searchResultsLookupKey={codableTransaction?.guid}
        debounceTimeout={200}
        fieldDefinitions={fieldDefinitions}
        handleCodeDelete={handleCodeDelete}
        onCodeSearch={onSearch}
        onCodeSearchSelection={onSearchSelection}
        editMode={editable}
        handleCodeChecked={handleCodeChecked}
        isSharedInvoice={true}
        suggestedCodes={suggestedCodes}
        onAddAllCodes={onAddAllCodes}
        onRemoveAllCodes={onRemoveAllCodes}
        codeDropHandler={codeDropHandler}
      />
    );
  } else {
    //@ts-ignore
    const shared = procedureCode?.shared ?? false;
    const sharedInvoice =
      invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES;
    const uneditableSharedCode = shared && !sharedInvoice;

    const allEdits = codeEdits[procedureCode?.code] ?? [];
    const vwErrors = allEdits.some(
      (edit) => edit.level === EditorIssueLevel.ERROR
    );
    const vwWarnings = allEdits.some(
      (edit) => edit.level === EditorIssueLevel.WARNING
    );

   const disablePro =  !editable || (techTransaction.hasProCode && !techTransaction.hasTechCode);
   const disableTech = !editable || (techTransaction.hasTechCode && !techTransaction.hasProCode);

    const styleClasses = [`transaction-headerV2`];
    const codeUnitsStyle = [
      'code-units-input',
      {
        'procedure-code-units-invalid': errors.some(
          (error) => error.message_key === 'procedure_code.units.invalid'
        )
      }
    ];

    const proClassName = classnames([
      {
        'is-active': techTransaction.isProCodeActive,
        'is-inactive': !techTransaction.isProCodeActive
      }
    ]);

    const techClassName = classnames([
      {
        'is-active': techTransaction.isTechCodeActive,
        'is-inactive': !techTransaction.isTechCodeActive
      }
    ]);

    const techIndicatorClassName = classnames([
      'tech-code-cell',
      'qa-tech-indicator',
      'prevent-click',
      'tech-indicator',
      'indicator',
      'indicator-pro-tech',
      {
        'tech-indicator-no-space':
          (techTransaction.hasProCode && techTransaction.isTechCodeActive) ||
          (techTransaction.hasTechCode && techTransaction.isProCodeActive),
        'indicator-pro-tech-active': techTransaction.isTechCodeActive,
        'indicator-pro-tech-inactive': !techTransaction.isTechCodeActive,
        'single-indicator-pro-tech': !techTransaction.hasProCode,
        'indicator-pro-tech-modifier': techTransaction.isModifier,
        'indicator-pro-tech-no-modifier': !techTransaction.isModifier,
        'pro-tech-loading': techTransaction.isLoading
      }
    ]);

    const proIndicatorClassName = classnames([
      'pro-code-cell',
      'qa-pro-indicator',
      'prevent-click',
      'pro-indicator',
      'indicator',
      'indicator-pro-tech',
      {
        'indicator-pro-tech-active': techTransaction.isProCodeActive,
        'indicator-pro-tech-inactive': !techTransaction.isProCodeActive,
        'single-indicator-pro-tech': !techTransaction.hasTechCode,
        'indicator-pro-tech-modifier': techTransaction.isModifier,
        'indicator-pro-tech-no-modifier': !techTransaction.isModifier,
        'pro-tech-loading': techTransaction.isLoading
      }
    ]);

    const proCheck = classnames([
      'pro-tech-check',
      {
        'pro-tech-check-active': techTransaction.isProCodeActive,
        'pro-tech-check-inactive': !techTransaction.isProCodeActive
      }
    ]);

    const techCheck = classnames([
      'pro-tech-check',
      {
        'pro-tech-check-active': techTransaction.isTechCodeActive,
        'pro-tech-check-inactive': !techTransaction.isTechCodeActive
      }
    ]);

    const toggleProCode = () => {
      if (!techTransaction.isLoading) {
        techTransaction.isProCodeActive
          ? handleEnableTechPro(techTransaction.proContext)
          : handleDisableTechPro(techTransaction.proContext);
      }
    };

    const toggleTechCode = () => {
      if (!techTransaction.isLoading) {
        techTransaction.isTechCodeActive
          ? handleEnableTechPro(techTransaction.techContext)
          : handleDisableTechPro(techTransaction.techContext);
      }
    };

    const handleUnitsUpdate = (units: number) => {
      const unitsDiv = unitsRef.current?.querySelector(
        '.unit-input'
      ) as HTMLElement;
      handleUnitsChange(units, unitsDiv);
    };
    const txnLedger = getLedgerTransaction(
      getLedgerTransactionsFromInvoice(invoice),
      codableTransaction?.guid
    );

    const {
      changedCptCodes,
      changedIcdCodes,
      changedModifiers
    } = getLedgerChangedCodes(txnLedger);
    return (
      <div
        {...(procedureCode && {
          'data-code': procedureCode.code,
          'data-code-type': procedureCode.codeType
        })}
        className={classnames(styleClasses)}>
        {/* disable transactions reordering, if a inovoice has a temporary transaction */}
        <Conditional
          on={
            editable &&
            invoice?.transactions.length > 1 &&
            invoice.transactions.every((txn) => !txn.isTemporary)
          }>
          <div className="transaction-code-gripper">
            <Menu placement="bottom-end">
              <MenuButton as={Box} cursor="pointer">
                <span className="code-gripper">
                  <CMXIcon
                    svgIconName="BiomeGripperSVG20"
                    svgIconSize="20"
                    svgIconClass="gripper"
                  />
                </span>
              </MenuButton>
              <MenuList className="transaction-reorder-menu">
                {!isFirstTnx && (
                  <>
                    <MenuItem
                      onClick={() =>
                        handleTransactionReorder(transactionIndex, 0)
                      }>
                      Move to Top
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleTransactionReorder(
                          transactionIndex,
                          transactionIndex - 1
                        )
                      }>
                      Move Up
                    </MenuItem>
                  </>
                )}
                {!isLastTnx && (
                  <>
                    <MenuItem
                      onClick={() =>
                        handleTransactionReorder(
                          transactionIndex,
                          transactionIndex + 1
                        )
                      }>
                      Move Down
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleTransactionReorder(
                          transactionIndex,
                          invoice?.transactions.length - 1
                        )
                      }>
                      Move to Bottom
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </Menu>
          </div>
        </Conditional>

        <div className="units-column" ref={unitsRef}>
          {procedureCode?.units && (
            <span className="code-units-populateable prevent-click">
              <UnitInput
                minLimit={1}
                value={procedureCode?.units}
                disabled={!editable || !procedureCode?.editable}
                onChange={handleUnitsUpdate}
              />
            </span>
          )}
        </div>
        <div className="procedure-code-section">
          <div
            className={`procedure-code-column 
    ${vwErrors ? 'vwError' : vwWarnings ? 'vwWarning' : ''}`}>
            <MedicalCodesManager
              codeType={ChargeSessionCodeType.PROCEDURE}
              codeList={procedureCode ? [procedureCode] : []}
              searchResults={searchResults?.[CodeSearchType.CPT]}
              searchResultsLookupKey={codableTransaction?.guid}
              debounceTimeout={200}
              fieldDefinitions={fieldDefinitions}
              handleCodeDelete={handleCodeDelete}
              onCodeSearch={onSearch}
              onCodeSearchSelection={onSearchSelection}
              editMode={editable}
              suggestedCodes={suggestedCodes}
              transactionGuid={codableTransaction?.guid}
              onAddAllCodes={onAddAllCodes}
              onRemoveAllCodes={onRemoveAllCodes}
              codeEdits={codeEdits}
              autoFocus={autoFocus}
              changedCodes={codeSourcesLedgerEnabled ? changedCptCodes : []}
            />
          </div>
          <div className="code-actions-column">
            {techTransaction.hasProCode && (
              <div className={classnames(proIndicatorClassName)}>
                <Button
                  aria-label=""
                  variant="clock"
                  className={proClassName}
                  onClick={toggleProCode}
                  disabled={disablePro}>
                  <CMXIcon
                    svgIconName="BiomeCheckSVG20"
                    svgIconSize="12"
                    svgIconClass={classnames(proCheck)}
                  />
                  <p className="pro-tech-label">P</p>
                </Button>
              </div>
            )}

            {techTransaction.hasTechCode && (
              <div className={classnames(techIndicatorClassName)}>
                <Button
                  aria-label=""
                  variant="clock"
                  className={techClassName}
                  onClick={toggleTechCode}
                  disabled={disableTech}>
                  <CMXIcon
                    svgIconName="BiomeCheckSVG20"
                    svgIconSize="12"
                    svgIconClass={classnames(techCheck)}
                  />
                  <p className="pro-tech-label">T</p>
                </Button>
              </div>
            )}

            {procedureCode?.codeType === 'procedure' && isTimeBasedCode() && (
              <div className="time-code-cell">{timeBasedCodePopup()}</div>
            )}
          </div>
        </div>
        <div className="modifier-codes-section">
          <MedicalCodesManager
            codeType={ChargeSessionCodeType.MODIFIER}
            codeList={modifierCodeList}
            searchResults={searchResults?.[CodeSearchType.MODIFIER]}
            searchResultsLookupKey={codableTransaction?.guid}
            debounceTimeout={200}
            fieldDefinitions={fieldDefinitions}
            handleCodeDelete={handleCodeDelete}
            onCodeSearch={onSearch}
            onCodeSearchSelection={onSearchSelection}
            editMode={editable}
            suggestedCodes={suggestedCodes}
            codeDropHandler={codeDropHandler}
            transactionGuid={codableTransaction?.guid}
            onAddAllCodes={onAddAllCodes}
            onRemoveAllCodes={onRemoveAllCodes}
            codeEdits={codeEdits}
            changedCodes={codeSourcesLedgerEnabled ? changedModifiers : []}
          />
        </div>
        <div className="diagnosis-codes-section">
          <MedicalCodesManager
            codeType={ChargeSessionCodeType.DIAGNOSIS}
            codeList={dianosisCodeList}
            searchResults={searchResults?.[CodeSearchType.ICD]}
            searchResultsLookupKey={codableTransaction?.guid}
            debounceTimeout={200}
            fieldDefinitions={fieldDefinitions}
            handleCodeDelete={handleCodeDelete}
            onCodeSearch={onSearch}
            onCodeSearchSelection={onSearchSelection}
            editMode={editable}
            handleCodeChecked={handleCodeChecked}
            codeDropHandler={codeDropHandler}
            suggestedCodes={suggestedCodes}
            transactionGuid={codableTransaction?.guid}
            onAddAllCodes={onAddAllCodes}
            onRemoveAllCodes={onRemoveAllCodes}
            codeEdits={codeEdits}
            changedCodes={codeSourcesLedgerEnabled ? changedIcdCodes : []}
          />
        </div>
      </div>
    );
  }
};
